import { useState } from "react";
import { CheckCircle2, Mail, MessageCircle } from "lucide-react";
import { Eyebrow, Reveal } from "./Reveal";
import { EMAIL, PHONE_DISPLAY, PHONE_TEL, WHATSAPP_URL } from "../../constants/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { name: "", phone: "", remark: "" };

const FORM_ENABLED = true;

const Field = ({ id, label, error, ...props }) => (
  <div>
    <label htmlFor={id} className="mb-1.5 block text-[0.625rem] font-medium uppercase tracking-[0.16em] text-sub">
      {label}
    </label>
    <input
      id={id}
      data-testid={id}
      disabled={!FORM_ENABLED}
      {...props}
      className={`w-full rounded-md border bg-base-color px-3.5 py-2.5 text-[0.9375rem] outline-none transition-colors placeholder:text-sub/50 focus:border-violet disabled:cursor-not-allowed disabled:opacity-60 ${
        error ? "border-red-500" : "border-hairline"
      }`}
    />
    {error && (
      <p className="mt-1 text-xs text-red-500" data-testid={`${id}-error`}>
        {error}
      </p>
    )}
  </div>
);

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");
  const [tab, setTab] = useState("callback");

  const set = (key) => (e) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    setErrors((er) => ({ ...er, [key]: undefined }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!FORM_ENABLED) return;
    const er = {};
    if (!form.name.trim()) er.name = "Please tell us your name.";
    if (form.phone.replace(/\D/g, "").length < 10) er.phone = "Enter a number we can call back — 10 digits or more.";
    setErrors(er);
    if (Object.keys(er).length) return;

    setStatus("sending");
    try {
      const res = await fetch(`${API}/callbacks`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("Request failed");
      setStatus("sent");
    } catch {
      setStatus("error");
    }
  };

  return (
    <section id="contact" className="border-t border-hairline py-12 sm:py-16" data-testid="contact-section">
      <div className="grid site-x grid-cols-1 gap-14 lg:grid-cols-2">
        <Reveal>
          <Eyebrow>Contact</Eyebrow>
          <h2 className="mt-4 font-head font-extrabold tracking-tight h-sec">
            Tell us what needs checking.
          </h2>
          <p className="mt-4 max-w-md text-base leading-relaxed text-sub">
            If it is urgent, calling is faster. The form is for anyone who
            cannot speak freely right now.
          </p>

          <div className="mt-10">
            <a
              href={PHONE_TEL}
              data-testid="contact-phone-link"
              className="font-head text-4xl font-extrabold tracking-tight transition-colors hover:text-violet sm:text-5xl"
            >
              {PHONE_DISPLAY}
            </a>
            <div className="mt-5 flex flex-wrap items-center gap-3">
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="whatsapp-button"
                className="inline-flex items-center gap-2 rounded-full border border-ink/25 px-4 py-2 text-sm font-semibold transition-colors hover:border-violet hover:text-violet"
              >
                <MessageCircle size={14} />
                WhatsApp
              </a>
              <a
                href={`mailto:${EMAIL}`}
                data-testid="email-button"
                className="inline-flex items-center gap-2 rounded-full border border-ink/25 px-4 py-2 text-sm font-semibold transition-colors hover:border-violet hover:text-violet"
              >
                <Mail size={14} />
                {EMAIL}
              </a>
            </div>
            <p className="mt-8 text-sm uppercase leading-relaxed tracking-[0.16em] text-sub">
              Weekend, evening and overnight appointments are available.
              <br />
              Attendance in unmarked vehicles, arranged to avoid identifying the visit.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.12}>
          <div className="border border-hairline bg-surface p-6 sm:p-8">
            <div className="mb-6 flex gap-2" role="tablist" aria-label="Contact options">
              <button
                role="tab"
                aria-selected={tab === "callback"}
                onClick={() => setTab("callback")}
                data-testid="contact-tab-callback"
                className={`rounded-full border px-4 py-1.5 text-sm font-semibold transition-colors ${
                  tab === "callback"
                    ? "border-violet text-ink"
                    : "border-ink/25 text-sub hover:text-ink"
                }`}
              >
                Call-back
              </button>
              <button
                role="tab"
                aria-selected={tab === "email"}
                onClick={() => setTab("email")}
                data-testid="contact-tab-email"
                className={`rounded-full border px-4 py-1.5 text-sm font-semibold transition-colors ${
                  tab === "email"
                    ? "border-violet text-ink"
                    : "border-ink/25 text-sub hover:text-ink"
                }`}
              >
                Email
              </button>
            </div>
            {tab === "email" ? (
              <div className="flex flex-col items-start gap-4 py-4" data-testid="contact-email-pane">
                <h3 className="font-head text-xl font-bold">Write to us.</h3>
                <p className="text-base leading-relaxed text-sub">
                  Say as much or as little as you like. The venue and a time
                  can be given on the call, after an NDA if you want one.
                </p>
                <a
                  href={`mailto:${EMAIL}`}
                  data-testid="contact-email-link"
                  className="inline-flex items-center gap-2 rounded-full bg-violet px-6 py-3 text-[0.9375rem] font-semibold text-[#1a1021] transition-colors hover:bg-violeth"
                >
                  <Mail size={15} />
                  {EMAIL}
                </a>
              </div>
            ) : status === "sent" ? (
              <div className="flex flex-col items-start gap-4 py-8" data-testid="callback-success-message">
                <CheckCircle2 size={28} className="text-violet" />
                <h3 className="font-head text-xl font-bold">Request received.</h3>
                <p className="text-base leading-relaxed text-sub">
                  Thank you, {form.name.split(" ")[0]}. We return calls within
                  one working day — from an unmarked number, if you prefer.
                </p>
                <button
                  onClick={() => { setForm(EMPTY); setStatus("idle"); }}
                  data-testid="callback-send-another-button"
                  className="mt-2 text-sm font-semibold text-violet underline-offset-4 hover:underline"
                >
                  Send another request
                </button>
              </div>
            ) : (
              <form onSubmit={submit} noValidate className="flex flex-col gap-5" data-testid="callback-form">
                <Field
                  id="callback-name-input"
                  label="Name"
                  value={form.name}
                  onChange={set("name")}
                  error={errors.name}
                  placeholder="Your name"
                  autoComplete="name"
                />
                <Field
                  id="callback-phone-input"
                  label="Phone"
                  type="tel"
                  value={form.phone}
                  onChange={set("phone")}
                  error={errors.phone}
                  placeholder="+91 ..."
                  autoComplete="tel"
                />
                <div>
                  <label htmlFor="callback-remark-input" className="mb-1.5 block text-[0.625rem] font-medium uppercase tracking-[0.16em] text-sub">
                    What needs checking — optional
                  </label>
                  <textarea
                    id="callback-remark-input"
                    data-testid="callback-remark-input"
                    rows={3}
                    disabled={!FORM_ENABLED}
                    value={form.remark}
                    onChange={set("remark")}
                    placeholder="A boardroom, a residence, a vehicle — say as much or as little as you like."
                    className="w-full resize-none rounded-md border border-hairline bg-base-color px-3.5 py-2.5 text-[0.9375rem] outline-none transition-colors placeholder:text-sub/50 focus:border-violet disabled:cursor-not-allowed disabled:opacity-60"
                  />
                  <p className="mt-1.5 text-xs leading-relaxed text-sub">
                    Describe what needs checking if you wish; name and phone
                    are enough to start. The venue and a time can be given on
                    the call, after an NDA if you want one.
                  </p>
                </div>
                {status === "error" && (
                  <p className="text-sm text-red-500" data-testid="callback-error-message">
                    Something went wrong on our side. Please call us instead — {PHONE_DISPLAY}.
                  </p>
                )}
                <button
                  type="submit"
                  disabled={status === "sending"}
                  data-testid="callback-submit-button"
                  className="w-full rounded-full bg-violet py-3 text-[0.9375rem] font-semibold text-[#1a1021] transition-colors hover:bg-violeth disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {status === "sending" ? "Sending…" : "Request a call back"}
                </button>
                <p className="text-center text-sm uppercase leading-relaxed tracking-[0.16em] text-sub">
                  We contact you within 12 hours at most of your enquiry.
                  <br />
                  Client names are never published on this website.
                </p>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
